import * as React from 'react';
import classnames from 'classnames';
import {FormattedMessage} from 'react-intl';

import { HeadlineFont, HeadlineLevel, LinkStyle, Margin, SITE } from 'features/ui/enums';
import {renderPurchase} from 'features/residenceExplorer/components/PriceListV2/renderers';

import './_cards.scss';

import {A} from '@atoms/LinksAndButtons/A';
import {P} from '@atoms/Text/P';
import {Icon} from '@atoms/Media/Icon';
import {ResidenceTags} from '@atoms/Tags/ResidenceTags';
import {SiteSpecificContent} from '@atoms/SiteSpecificContent';
import {getCurrentSite, getLocalizedKvm, getLocalizedPrice, getLocalizedRok} from "features/services/utils/env-helper";
import {DisposalForm, ObjectStatus, ResidenceType} from "features/search/filters/enums";
import {getFormattedMessage} from "features/services/utils/language-helper";
import {renderInhabitDate} from "features/services/utils/date-helpers";
import {useAuth} from "features/authentication/hooks/useAuth";
import {Headline} from '@atoms/Text/Headline';

interface Props {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- generic table row data
    object: any;
    active: boolean;
}

export const PriceListCard: React.FunctionComponent<Props> = ({object, active}) => {
    const {user} = useAuth();

    const getResidenceTitle =() =>{
        if(getCurrentSite() === SITE.FI && typeof object.jmoyNumberOfRoomsExtraInfo !== 'undefined' && object.jmoyNumberOfRoomsExtraInfo !== null ) {
            return `${object.jmoyNumberOfRoomsExtraInfo}, ${getLocalizedKvm(object.primaryArea)}`
        }

        if(getCurrentSite() === SITE.NO && object.jmnoBraInternal !== null && object.jmnoBraInternal !== undefined) {
            return `${getLocalizedRok(object.rooms)}, ${getLocalizedKvm(object.jmnoBraInternal)}`;
        }

        if (object.residenceType == ResidenceType.PLOT) {
            return `${getLocalizedKvm(object.plotArea)}`;
        }

        return `${getLocalizedRok(object.rooms)}, ${getLocalizedKvm(object.primaryArea)}`;
    };
    const getTotalPrice = () => {
        let price = object.price;

        if(object.otherExpenses) {
            price += object.otherExpenses;
        }

        if(object.jmnoFellesgjeld && object.residenceType === ResidenceType.APARTMENT && (object.disposalForm === DisposalForm.OWNERSHIP || object.disposalForm === DisposalForm.OWNERSHIP_APARTMENT)) {
            price += object.jmnoFellesgjeld;
        }

        return getLocalizedPrice(price);
    };
    // Samma nummer som tabellraden. Saknas det står kortets rubrik (name) i stället — utan värde kastar react-intl och visar platshållaren.
    const linkText = getFormattedMessage('jm.components.Object.goToObject', {objectNumber: object.apartmentNumber || object.name});
    const shouldRenderFloor = () => {
        return (object.floor !== 0 && object.floor !== null && typeof object.floor !== 'undefined' &&
            object.maxFloor !== 0 && object.maxFloor !== null&& typeof object.maxFloor !== 'undefined');
    };

    const classes = classnames('grid-table__card', {
        'grid-table__card--active': active,
        'grid-table__card--with-tag': object.objectStatus,
    });

    return(
        <div className={classes}>
            {object.objectStatus && <ResidenceTags objectStatus={object.objectStatus} />}
            <P margin={Margin.S}>
                {user && renderPurchase(user)(object.id)}
                {object.name}
            </P>
            <Headline level={HeadlineLevel.H4} font={HeadlineFont.SERIF}>{getResidenceTitle()}</Headline>
            <dl>
                {object.objectStatus !== ObjectStatus.SOLD && object.objectStatus !== ObjectStatus.BOOKED &&
                    <>
                        {object.jmoyTotalPrice &&
                            <SiteSpecificContent showOnFinland>
                                <dt>Velaton hinta</dt>
                                <dd>{getLocalizedPrice(object.jmoyTotalPrice)}</dd>
                            </SiteSpecificContent>
                        }
                        <SiteSpecificContent notFinland>
                            <dt>Pris</dt>
                            <dd>{getLocalizedPrice(object.price)}</dd>
                        </SiteSpecificContent>
                        <SiteSpecificContent showOnNorway>
                                <dt>Totalpris inkl. omk.</dt>
                                <dd>{getTotalPrice()}</dd>
                        </SiteSpecificContent>
                    </>
                }
                {object.objectStatus !== ObjectStatus.SOLD && object.objectStatus !== ObjectStatus.BOOKED && (object.fee || object.jmnoSharedCost) &&
                    <>
                        <dt>
                            {(object.disposalForm as DisposalForm) === DisposalForm.OWNERSHIP || (object.disposalForm) === DisposalForm.OWNERSHIP_APARTMENT
                                ? getFormattedMessage('jm.components.QuickFacts.communityFee')
                                : getFormattedMessage('jm.components.QuickFacts.fee')}
                        </dt>
                        {object.fee && <dd>{getLocalizedPrice(object.fee)}</dd>}
                        {object.jmnoSharedCost && <dd>{getLocalizedPrice(object.jmnoSharedCost)}</dd>}
                    </>
                }
                {(shouldRenderFloor()) &&
                    <>
                        <dt>
                            <FormattedMessage id="jm.components.ObjectCard.floor" />
                        </dt>
                        <dd>
                            {object.floor}/{object.maxFloor}
                        </dd>
                    </>
                }
                {object.inhabitDate &&
                    <>
                        <dt><FormattedMessage id="jm.components.QuickFacts.inhabitDate" /></dt>
                        <dd>{renderInhabitDate(object.inhabitDate, object.inhabitDateEnd, object.inhabitDateDetermined)}</dd>
                    </>
                }
            </dl>
            {object.url &&
                <>
                    <Icon name="arrow-link" up size={24} />
                    <A href={object.url} route variant={LinkStyle.UNSTYLED} title={linkText} >
                        <span className='visually-hidden'>{linkText}</span>
                    </A>
                </>
            }
        </div>
    )
};
